// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// D47 (owner, 2026-09-18): "Same problem as an earlier report?" — pick the report
// this one is another instance of. Used by the report form and by a filed report.
//
// With nothing typed it lists the open reports filed from the same screen, newest
// first; a search reaches every report you may see, Fixed ones included (marked by
// their badge). The server decides what is offered (listLinkCandidates): never the
// report being linked, nor the reports already linked to it. Picking a report that
// is itself linked lands on its original, and the row says so.

import React, { useEffect, useState } from 'react';
import { listLinkCandidates, type BugLinkCandidate } from '../../lib/bugReportApi';
import { formatDate } from '../../utils/dateFormat';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import { ErrorState, LoadingIndicator } from '../../components/ui/States';
import { FOCUS } from '../../components/ui/tokens';
import { BugStatusBadge } from './BugStatusBadge';

const SEARCH_DEBOUNCE_MS = 300;

export const LinkPicker: React.FC<{
  /** The report being linked; absent in the form, where it does not exist yet. */
  forId?: string;
  /** The screen the report was filed from: its open reports are offered first. */
  screen: string;
  onPick: (candidate: BugLinkCandidate) => void;
  onCancel: () => void;
}> = ({ forId, screen, onPick, onCancel }) => {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<BugLinkCandidate[] | null>(null);
  const [limit, setLimit] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setCandidates(null);
    setError(null);
    listLinkCandidates({ forId, screen, q: query })
      .then((res) => {
        if (cancelled) return;
        setCandidates(res.data.candidates);
        setLimit(res.data.limit);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'The reports could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [forId, screen, query, attempt]);

  const heading = query ? 'Reports that match' : screen ? `Open reports from ${screen}` : 'Open reports';

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search title or ticket number"
        aria-label="Search earlier reports"
        autoFocus
      />
      <p className="text-xs font-medium text-slate-500">{heading}</p>

      {error ? (
        <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : !candidates ? (
        <LoadingIndicator label="Loading reports…" />
      ) : candidates.length === 0 ? (
        <p className="text-sm text-slate-500">
          {query
            ? 'No report you can see matches that.'
            : `No open reports from ${screen || 'this screen'} yet. Search to find one from another screen.`}
        </p>
      ) : (
        <ul aria-label="Earlier reports" className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
          {candidates.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onPick(c)}
                className={`block w-full px-1 py-2 text-left hover:bg-slate-50 ${FOCUS}`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-slate-500">{c.ticketNumber}</span>
                  <BugStatusBadge status={c.status} />
                </span>
                <span className="mt-1 line-clamp-2 block break-words text-sm font-medium leading-snug text-slate-900">
                  {c.title}
                </span>
                <span className="mt-0.5 block truncate text-xs text-slate-500">
                  {[c.pageLabel || null, c.isMine ? 'You' : null, formatDate(c.createdAt) || null].filter(Boolean).join(' · ')}
                </span>
                {c.linkedToTicket ? (
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Already linked to {c.linkedToTicket} — picking it links to {c.linkedToTicket}.
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}

      {candidates && candidates.length >= limit && limit > 0 ? (
        <p className="text-xs text-slate-500">Showing the {limit} newest. Search to find an older one.</p>
      ) : null}

      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
